import React, { useCallback } from 'react';

import { ExtendedForecastListItem } from '@futrli/views/processors/forecasts/forecastsList';
import { useIntl } from '@futrli/localisation';
import { VirtualizedCardList } from '@futrli/predict/ui/virtualized-card-list/virtualized-card-list';
import { VirtualizedCardListEmptyState } from '@futrli/predict/ui/virtualized-card-list/virtualized-card-list-empty-state';

import { CreateForecastPopover } from '../create-forecast-popover/create-forecast-popover';
import {
  MyForecastRow,
  ROW_HEIGHT_DEFAULT,
  ROW_HEIGHT_WITH_LOCKED_PILL
} from './components/my-forecast-row/my-forecast-row';
import { useForecastActions } from '../../context/forecast-actions-context/forecast-actions-context';

interface MyForecastListProps {
  data?: ExtendedForecastListItem[];
  isConsolidation?: boolean;
  isEmpty?: boolean;
  isLoading?: boolean;
  isResourceSharingEnabled?: boolean;
}

export const MyForecastList = ({
  data = [],
  isConsolidation = false,
  isEmpty = false,
  isLoading = false,
  isResourceSharingEnabled = false
}: MyForecastListProps) => {
  const intl = useIntl();
  const { handleNameClick } = useForecastActions();

  const renderRow = useCallback(
    (item: ExtendedForecastListItem) => {
      return (
        <MyForecastRow
          forecast={item}
          onNameClick={() => handleNameClick(item, isConsolidation)}
          isResourceSharingEnabled={isResourceSharingEnabled}
        />
      );
    },
    [handleNameClick, isConsolidation, isResourceSharingEnabled]
  );

  const rowHeight = useCallback(
    ({ index }: { index: number }) => (data[index]?.is_frozen ? ROW_HEIGHT_WITH_LOCKED_PILL : ROW_HEIGHT_DEFAULT),
    [data]
  );

  return (
    <VirtualizedCardList<ExtendedForecastListItem>
      data={data}
      renderRow={renderRow}
      rowHeight={rowHeight}
      isLoading={isLoading}
      emptyState={
        <VirtualizedCardListEmptyState
          title={intl.formatMessage({ id: 'predict.no_forecasts', defaultMessage: 'No forecasts to show' })}
          description={
            isEmpty
              ? intl.formatMessage({
                  id: 'predict.get_started_with_forecast',
                  defaultMessage: 'Get started with your first forecast'
                })
              : undefined
          }
          action={isEmpty ? <CreateForecastPopover variant="secondary" /> : undefined}
        />
      }
    />
  );
};
